import { useEffect, useRef, useState } from "react";
import Marquee from "react-fast-marquee";
import Lenis from "lenis";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue,
  AnimatePresence,
} from "framer-motion";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { ArrowRight, Check, HeartHandshake, BookOpen, Video, X } from "lucide-react";
import { LogoMark } from "@/components/LogoMark";
import { EASE, MaskedLine, Reveal } from "@/components/motion";
import { ContactButton } from "@/components/ContactDialog";
import { createOrder, createDonation, getDonationStats, newsletterSignup, createCheckout, claimEbook } from "@/lib/api";

const IMAGES = {
  hero: "/cover-hero.jpg",
  ebook: "/cover-hero.jpg",
  gambling: "/cover-gambling.jpg",
  video: "https://static.prod-images.emergentagent.com/jobs/eb113aaa-6c78-476f-b1e6-16f1ab4478fc/images/592a4b9bc11d5f599fdf22b4d406c2eb15f0e6e506ffaa486bfc984ea691c152.png",
  testimonial1: "https://images.unsplash.com/photo-1596075780750-81249df16d19?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  testimonial2: "https://images.pexels.com/photos/1486057/pexels-photo-1486057.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  bio: "https://customer-assets.emergentagent.com/job_poker-freedom-hub/artifacts/4erjazvt_IMG_4089.jpeg",
};

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`w-full ${className}`}>{children}</section>
);

/* ---------------- Signature on-load moment ---------------- */

const IntroOverlay = ({ onDone }) => (
  <motion.div
    className="fixed inset-0 z-[100] bg-[#0A0A0A] flex flex-col items-center justify-center gap-6"
    initial={{ y: 0 }}
    animate={{ y: "-100%" }}
    transition={{ duration: 0.85, delay: 1.15, ease: EASE }}
    onAnimationComplete={onDone}
    data-testid="intro-overlay"
  >
    <motion.div
      initial={{ scale: 0.6, opacity: 0, rotate: -8 }}
      animate={{ scale: 1, opacity: 1, rotate: 0 }}
      transition={{ duration: 0.6, ease: EASE }}
    >
      <LogoMark size={72} />
    </motion.div>
    <div className="overflow-hidden">
      <motion.div
        className="font-display text-white uppercase tracking-[0.3em] text-sm sm:text-base"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.7, delay: 0.25, ease: EASE }}
      >
        The Last Bet Project
      </motion.div>
    </div>
    <motion.div
      className="h-0.5 bg-[#E60000]"
      initial={{ width: 0 }}
      animate={{ width: 120 }}
      transition={{ duration: 0.7, delay: 0.35, ease: EASE }}
    />
  </motion.div>
);

/* ---------------- Header ---------------- */

const Header = ({ onDonate }) => (
  <motion.header
    className="sticky top-0 z-50 w-full bg-white border-b-2 border-black"
    data-testid="site-header"
    initial={{ y: -80 }}
    animate={{ y: 0 }}
    transition={{ duration: 0.8, delay: 1.5, ease: EASE }}
  >
    <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-12 py-4">
      <a href="#top" className="flex items-center gap-3" data-testid="logo-link">
        <LogoMark size={30} />
        <span className="font-display text-lg sm:text-2xl uppercase tracking-tight whitespace-nowrap">
          The Last <span className="text-[#E60000]">Bet</span> Project
        </span>
      </a>
      <nav className="hidden md:flex items-center gap-8 text-sm font-semibold uppercase tracking-widest">
        <a href="#products" data-testid="nav-products" className="hover:text-[#E60000] transition-colors">Get Help</a>
        <a href="#author" data-testid="nav-stories" className="hover:text-[#E60000] transition-colors">Story</a>
        <a href="#author" data-testid="nav-author" className="hover:text-[#E60000] transition-colors">Author</a>
        <a href="#faq" data-testid="nav-faq" className="hover:text-[#E60000] transition-colors">FAQ</a>
        <Link to="/gambling" data-testid="nav-gambling" className="hover:text-[#E60000] transition-colors">Gambling Help</Link>
      </nav>
      <div className="flex items-center gap-3">
        <ContactButton className="hidden sm:inline-flex" />
        <ContactButton iconOnly className="sm:hidden" />
        <motion.button
          onClick={onDonate}
          data-testid="header-donate-btn"
          whileTap={{ scale: 0.95 }}
          className="border-2 border-black bg-white px-4 py-2 font-bold uppercase tracking-widest text-sm hover:bg-[#E60000] hover:text-white transition-colors"
        >
          Donate
        </motion.button>
      </div>
    </div>
  </motion.header>
);

/* ---------------- Hero: kinetic type + 3D parallax book ---------------- */

const HERO_LINES = [
  { text: "I know,", accent: false },
  { text: "the pain,", accent: true },
  { text: "the struggle,", accent: true },
  { text: "the suffering.", accent: true },
];

const BOOK_TITLES = {
  ebook: "The Secret to Stop Poker Addiction",
  ebook_gambling: "The Secret to Stop Gambling Addiction",
};

/* Self-contained book cover panel: spotlight + scroll parallax + mouse tilt */
const CoverPanel = ({ img, label, delay = 1.4, testId, className = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 16 });
  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };
  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={`relative bg-black flex items-center justify-center overflow-hidden ${className}`}
    >
      <div className="absolute inset-0 spotlight" aria-hidden="true" />
      <div className="absolute top-4 left-4 z-20 bg-[#E60000] text-white text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5 border-2 border-black">
        {label}
      </div>
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
        className="w-full h-full flex items-center justify-center"
        style={{ perspective: 1100 }}
      >
        <motion.img
          src={img}
          alt={label}
          data-testid={testId}
          className="w-full h-full object-contain object-center will-change-transform"
          style={{ y: parallaxY, rotateX, rotateY, transformStyle: "preserve-3d", scale: 1.06 }}
        />
      </motion.div>
    </div>
  );
};

const Hero = () => (
  <Section id="top" className="border-b-2 border-black">
    <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-0">
      <div className="lg:col-span-6 lg:order-2 px-6 lg:px-10 pt-8 pb-10 lg:pt-10 lg:pb-12 lg:max-h-[88vh] overflow-hidden">
        <motion.div
          className="inline-flex items-center gap-3 border-2 border-black bg-white px-4 py-2 mb-8 max-w-full"
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, x: -32 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 1.55, ease: EASE }}
        >
          <span className="w-2.5 h-2.5 bg-[#E60000] inline-block flex-shrink-0" />
          <span className="sm:whitespace-nowrap text-[10px] sm:text-xs md:text-sm lg:text-[0.95rem] uppercase tracking-[0.12em] font-bold">Two free books. One way out.</span>
        </motion.div>
        <h1
          className="font-display uppercase leading-[0.95] tracking-wide text-5xl sm:text-6xl lg:text-[4.6vw]"
          data-testid="hero-title"
        >
          {HERO_LINES.map((line, i) => (
            <MaskedLine key={line.text} delay={1.65 + i * 0.13}>
              {line.accent ? (
                <span className="text-[#E60000]">{line.text}</span>
              ) : (
                line.text
              )}
            </MaskedLine>
          ))}
        </h1>
        <div className="overflow-hidden mt-6">
          <motion.p
            className="font-script normal-case leading-[1] tracking-normal text-4xl sm:text-5xl lg:text-6xl text-[#E60000]"
            data-testid="hero-subhead-2"
            initial={{ y: "110%", rotate: 2 }}
            animate={{ y: "0%", rotate: 0 }}
            transition={{ duration: 0.9, delay: 2.25, ease: EASE }}
          >
            There is something you need to know.
          </motion.p>
        </div>
        <motion.div
          className="mt-8 flex flex-col sm:flex-row gap-4"
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 2.5, ease: EASE }}
        >
          <motion.a
            href="#products"
            data-testid="hero-cta-primary"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.96 }}
            className="inline-flex items-center justify-center gap-2 bg-[#E60000] text-white border-2 border-black px-8 py-4 font-bold uppercase tracking-widest text-sm shadow-brutal-sm shadow-brutal-hover"
          >
            Get Your Free Book <ArrowRight className="w-4 h-4" />
          </motion.a>
          <motion.a
            href="#author"
            data-testid="hero-cta-secondary"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.96 }}
            className="inline-flex items-center justify-center bg-white text-black border-2 border-black px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-black hover:text-white transition-colors"
          >
            Read My Story
          </motion.a>
        </motion.div>
        <motion.div
          className="mt-8 grid grid-cols-3 gap-0 border-2 border-black max-w-md"
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 2.7, ease: EASE }}
        >
          <div className="p-4 border-r-2 border-black">
            <div className="font-display text-3xl">2</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-700">Free books</div>
          </div>
          <div className="p-4 border-r-2 border-black">
            <div className="font-display text-3xl">3K</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-700">Quit so far</div>
          </div>
          <div className="p-4">
            <div className="font-display text-3xl">Free</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-700">Download</div>
          </div>
        </motion.div>
      </div>
      <div className="grid grid-cols-2 lg:contents">
        <CoverPanel
          img={IMAGES.hero}
          label="Poker Edition"
          testId="hero-image"
          delay={1.4}
          className="lg:order-1 lg:col-span-3 aspect-[3/4] lg:aspect-auto lg:min-h-[60vh] lg:max-h-[88vh] border-r-2 border-black"
        />
        <CoverPanel
          img={IMAGES.gambling}
          label="Gambling Edition"
          testId="hero-image-gambling"
          delay={1.55}
          className="lg:order-3 lg:col-span-3 aspect-[3/4] lg:aspect-auto lg:min-h-[60vh] lg:max-h-[88vh] lg:border-l-2 border-black"
        />
      </div>
    </div>
  </Section>
);

/* ---------------- Slow editorial marquee ---------------- */

const MarqueeBand = () => (
  <Section className="bg-[#E60000] text-white border-b-2 border-black">
    <Marquee speed={30} gradient={false} className="py-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <span key={i} className="font-display text-5xl lg:text-7xl uppercase mx-8 flex items-center gap-8">
          Reclaim Your Mind.
          <span className="text-outline-white">Reclaim Your Wallet.</span>
          No More Excuses.
          <span className="w-3 h-3 bg-white inline-block" />
        </span>
      ))}
    </Marquee>
  </Section>
);

const PRICES = { ebook: "Free" };

const ProductCard = ({ id, kind, title, subtitle, image, points, price, selected, onSelect, testId }) => (
  <button
    onClick={() => onSelect(id)}
    data-testid={testId}
    className={`text-left border-2 border-black bg-white p-6 lg:p-8 transition-all relative w-full ${
      selected ? "shadow-brutal-red" : "shadow-brutal-sm hover:shadow-brutal"
    }`}
    aria-pressed={selected}
  >
    {selected && (
      <div className="absolute -top-3 -right-3 bg-[#E60000] text-white border-2 border-black w-10 h-10 flex items-center justify-center">
        <Check className="w-5 h-5" strokeWidth={3} />
      </div>
    )}
    <div className="flex items-center gap-3 mb-6">
      <div className="border-2 border-black bg-black text-white p-2">
        {kind === "ebook" ? <BookOpen className="w-5 h-5" /> : <Video className="w-5 h-5" />}
      </div>
      <div className="text-xs uppercase tracking-[0.25em] font-bold">{kind === "ebook" ? "Read it" : "Watch it"}</div>
    </div>
    <div className="aspect-[4/5] w-full border-2 border-black overflow-hidden mb-6 bg-black relative">
      <div className="absolute inset-0 spotlight z-10 pointer-events-none" aria-hidden="true" />
      <img src={image} alt={title} className="w-full h-full object-contain" />
    </div>
    <h3 className="font-display text-3xl lg:text-4xl uppercase leading-none">{title}</h3>
    <p className="mt-3 text-neutral-700 font-medium">{subtitle}</p>
    <ul className="mt-6 space-y-2">
      {points.map((p) => (
        <li key={p} className="flex items-start gap-2 text-sm">
          <span className="mt-1 w-2 h-2 bg-[#E60000] inline-block flex-shrink-0" />
          <span>{p}</span>
        </li>
      ))}
    </ul>
    <div className="mt-6 flex items-baseline justify-between border-t-2 border-black pt-4">
      <span className="font-display text-5xl">{price === "Free" ? "Free" : `$${price}`}</span>
      <span className="text-xs uppercase tracking-widest font-bold">Instant download</span>
    </div>
  </button>
);

const Products = ({ onBuy }) => {
  const [selected, setSelected] = useState("ebook");
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const { session_id } = await claimEbook({
        customer_name: form.name.trim(),
        customer_email: form.email.trim(),
        package_id: selected,
      });
      onBuy({ name: form.name.trim(), email: form.email.trim(), session_id, title: BOOK_TITLES[selected] });
      setSubmitting(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Section id="products" className="border-b-2 border-black">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#E60000] mb-4">Step 01 — Get the books</div>
              <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9]">
                Two books.<br /><span className="text-[#E60000]">Both free.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <Reveal delay={0.15}>
              <p className="text-lg text-neutral-700 font-medium">
                One decision. Pick the book that meets you where you are — poker,
                or the whole gambling machine — and we'll send the full manual
                straight to your inbox so you can start tonight.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <Reveal delay={0.1}>
            <ProductCard
              id="ebook"
              kind="ebook"
              title="The Secret to Stop Poker Addiction"
              subtitle="The manual to crush the urge, rebuild your bankroll-of-life, and never go back to the felt again."
              image={IMAGES.ebook}
              points={[
                "12-chapter recovery framework",
                "Daily craving-defeat scripts",
                "Financial reset workbook",
                "PDF, instant delivery",
              ]}
              price={PRICES.ebook}
              selected={selected === "ebook"}
              onSelect={setSelected}
              testId="product-card-ebook"
            />
          </Reveal>
          <Reveal delay={0.2}>
            <ProductCard
              id="ebook_gambling"
              kind="ebook"
              title="The Secret to Stop Gambling Addiction"
              subtitle="The same raw truth, aimed at the whole machine — slots, sports betting, blackjack, roulette, and every bet in between."
              image={IMAGES.gambling}
              points={[
                "Recovery framework for every game",
                "Spot the machine's hooks",
                "Financial reset workbook",
                "PDF, instant delivery",
              ]}
              price={PRICES.ebook}
              selected={selected === "ebook_gambling"}
              onSelect={setSelected}
              testId="product-card-gambling"
            />
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            className="mt-12 border-2 border-black p-6 lg:p-10 bg-white"
            data-testid="checkout-form"
          >
            <p className="mb-6 text-xs uppercase tracking-[0.25em] font-bold" data-testid="checkout-selected-book">
              Sending: <span className="text-[#E60000]">{BOOK_TITLES[selected]}</span>
              <span className="text-neutral-500 normal-case tracking-normal font-medium"> — tap the other book above to switch</span>
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
              <div className="lg:col-span-4">
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.25em] font-bold mb-2 inline-block">Your name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Alex Carter"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="checkout-name-input"
                  className="rounded-none border-2 border-black h-14 text-lg focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#E60000]"
                />
              </div>
              <div className="lg:col-span-4">
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.25em] font-bold mb-2 inline-block">Email — we deliver here</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="alex@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="checkout-email-input"
                  className="rounded-none border-2 border-black h-14 text-lg focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#E60000]"
                />
              </div>
              <div className="lg:col-span-4">
                <motion.button
                  type="submit"
                  disabled={submitting}
                  data-testid="checkout-submit-btn"
                  whileTap={{ scale: 0.97 }}
                  className="w-full h-14 bg-[#E60000] text-white border-2 border-black font-bold uppercase tracking-widest text-sm shadow-brutal-sm shadow-brutal-hover flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? "Preparing your book..." : <>Send Me The Book — Free <ArrowRight className="w-4 h-4" /></>}
                </motion.button>
              </div>
            </div>
            <p className="mt-4 text-xs uppercase tracking-widest font-bold text-neutral-600">
              Free download — no card, no catch. We'll also email you the link so you have it forever.
            </p>
          </form>
        </Reveal>
      </div>
    </Section>
  );
};

const Donation = ({ open, setOpen, stats, refreshStats }) => {
  const [amount, setAmount] = useState("");
  const [frequency, setFrequency] = useState("one_time");
  const [monthlyTier, setMonthlyTier] = useState(20);
  const [anonymous, setAnonymous] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  // Monthly: live Stripe tiers $10/$20/$50/$100. One-time: custom amount via dynamic price_data.
  const MONTHLY_TIERS = [10, 20, 50, 100];
  const PRESETS = [10, 25, 50, 100];

  // Arriving via the email's Donate button (/#donate): pre-select $10 so the form is ready
  useEffect(() => {
    if (window.location.hash === "#donate" && !amount) {
      setAmount("10");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    let package_id;
    let bodyExtra = {};
    if (frequency === "monthly") {
      package_id = `donation_monthly_${monthlyTier}`;
    } else {
      const amt = parseFloat(amount);
      if (!amt || amt <= 0) {
        toast.error("Enter an amount greater than 0.");
        return;
      }
      package_id = "donation_onetime_custom";
      bodyExtra = { amount: amt };
    }
    setSubmitting(true);
    try {
      const { checkout_url } = await createCheckout({
        package_id,
        customer_name: form.name || null,
        customer_email: form.email || null,
        message: form.message || null,
        ...bodyExtra,
      });
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Donation failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <Section id="donate" className="bg-black text-white border-b-2 border-black">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="inline-flex items-center gap-2 border-2 border-white px-3 py-1 mb-6">
                <HeartHandshake className="w-4 h-4 text-[#E60000]" />
                <span className="text-xs uppercase tracking-[0.25em] font-bold">Support our cause to help others</span>
              </div>
              <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9]">
                Keep this <br />
                <span className="text-[#E60000]">mission</span> alive.
              </h2>
              <p className="mt-6 max-w-xl text-lg text-neutral-300">
                We're not a clinic with millions of dollars at our disposal.
                We're a small team funded entirely by readers, donors, and our
                own dime. Every dollar keeps the lights on and helps us build a
                bigger project to help thousands of people break free from this
                diabolical disease — for free, and hopefully forever.
              </p>
              <p className="mt-4 max-w-xl text-base text-neutral-400 font-medium">
                Give once — or become a monthly supporter and fund a seat at the
                table every single month.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <motion.button
                onClick={() => setOpen(true)}
                data-testid="open-donate-dialog-btn"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.96 }}
                className="mt-10 inline-flex items-center gap-2 bg-[#E60000] text-white border-2 border-white px-8 py-4 font-bold uppercase tracking-widest text-sm shadow-brutal-hover hover:shadow-none"
              >
                <HeartHandshake className="w-4 h-4" /> Donate — One-Time or Monthly
              </motion.button>
            </Reveal>
          </div>
          <div className="lg:col-span-5 self-center">
            <Reveal delay={0.2}>
              <div className="border-2 border-white">
                <div className="p-8">
                  <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#E60000]">Reach</div>
                  <div className="font-display text-3xl mt-2 leading-tight">Helping people break the cycle, one player at a time.</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="rounded-none border-2 border-black bg-white p-0 max-w-lg shadow-brutal max-h-[92vh] overflow-y-auto"
          data-testid="donation-dialog"
          hideClose
        >
          <button
            onClick={() => setOpen(false)}
            className="absolute right-3 top-3 border-2 border-black w-9 h-9 flex items-center justify-center bg-white hover:bg-[#E60000] hover:text-white transition-colors"
            aria-label="Close"
            data-testid="donation-dialog-close"
          >
            <X className="w-4 h-4" />
          </button>
          <DialogHeader className="p-6 pr-16 border-b-2 border-black">
            <DialogTitle className="font-display text-3xl uppercase leading-none">Support our cause to help others</DialogTitle>
            <DialogDescription className="text-neutral-700 mt-2">
              Secure Stripe checkout — you'll be redirected to Stripe's encrypted page to complete your donation.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-0 border-2 border-black" data-testid="donate-frequency-tabs">
              <button
                type="button"
                onClick={() => { setFrequency("one_time"); setAmount(""); }}
                data-testid="donate-tab-one-time"
                className={`py-3 font-bold uppercase tracking-widest text-xs border-r-2 border-black transition-colors ${frequency === "one_time" ? "bg-[#E60000] text-white" : "bg-white text-black hover:bg-neutral-100"}`}
              >
                One-Time
              </button>
              <button
                type="button"
                onClick={() => { setFrequency("monthly"); setAmount(""); }}
                data-testid="donate-tab-monthly"
                className={`py-3 font-bold uppercase tracking-widest text-xs transition-colors ${frequency === "monthly" ? "bg-[#E60000] text-white" : "bg-white text-black hover:bg-neutral-100"}`}
              >
                Monthly
              </button>
            </div>
            {frequency === "monthly" ? (
              <>
                <div className="grid grid-cols-4 gap-2" data-testid="donate-monthly-tiers">
                  {MONTHLY_TIERS.map((t) => (
                    <button
                      type="button"
                      key={t}
                      onClick={() => setMonthlyTier(t)}
                      data-testid={`donate-monthly-tier-${t}`}
                      className={`py-3 border-2 border-black font-display text-2xl transition-all ${monthlyTier === t ? "bg-black text-white" : "bg-white text-black hover:bg-neutral-100"}`}
                    >
                      ${t}
                    </button>
                  ))}
                </div>
                <div className="border-2 border-black p-5 bg-[#F5F2EC]" data-testid="donate-monthly-info">
                  <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#E60000]">Monthly supporter</div>
                  <div className="font-display text-5xl mt-1">${monthlyTier}<span className="text-xl text-neutral-700">/month</span></div>
                  <p className="text-sm text-neutral-700 mt-2 font-medium">
                    You'll be charged ${monthlyTier} every month. Cancel anytime from the Stripe-hosted supporter portal we email you.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-2" data-testid="donate-preset-tiers">
                  {PRESETS.map((p) => (
                    <button
                      type="button"
                      key={p}
                      onClick={() => setAmount(String(p))}
                      data-testid={`donate-preset-${p}`}
                      className={`py-3 border-2 border-black font-display text-2xl transition-all ${parseFloat(amount) === p ? "bg-black text-white" : "bg-white text-black hover:bg-neutral-100"}`}
                    >
                      ${p}
                    </button>
                  ))}
                </div>
                <div>
                  <Label htmlFor="amount" className="text-xs uppercase tracking-[0.25em] font-bold">Amount (USD)</Label>
                  <div className="flex items-center mt-2 border-2 border-black">
                    <span className="px-4 font-display text-2xl bg-black text-white py-3">$</span>
                    <input
                      id="amount"
                      type="number"
                      step="0.01"
                      min="1"
                      placeholder="25.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      data-testid="donate-amount-input"
                      className="flex-1 h-12 px-4 text-lg outline-none bg-white"
                    />
                  </div>
                </div>
              </>
            )}
            <button
              type="button"
              onClick={() => { setAnonymous(!anonymous); setForm({ name: "", email: "", message: "" }); }}
              data-testid="donate-anonymous-toggle"
              aria-pressed={anonymous}
              className={`w-full flex items-center gap-3 p-3 border-2 border-black text-left transition-colors ${anonymous ? "bg-black text-white" : "bg-white hover:bg-neutral-100"}`}
            >
              <span className={`w-5 h-5 border-2 flex-shrink-0 flex items-center justify-center ${anonymous ? "border-white bg-[#E60000]" : "border-black"}`}>
                {anonymous && <span className="w-2.5 h-2.5 bg-white" />}
              </span>
              <span className="font-bold uppercase tracking-widest text-xs">Prefer to stay anonymous — no name, no email stored</span>
            </button>
            {!anonymous && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="dname" className="text-xs uppercase tracking-[0.25em] font-bold">Name (optional)</Label>
                <Input
                  id="dname"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="donate-name-input"
                  className="rounded-none border-2 border-black h-11 mt-2 focus-visible:ring-0 focus-visible:border-[#E60000]"
                />
              </div>
              <div>
                <Label htmlFor="demail" className="text-xs uppercase tracking-[0.25em] font-bold">Email (optional)</Label>
                <Input
                  id="demail"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="donate-email-input"
                  className="rounded-none border-2 border-black h-11 mt-2 focus-visible:ring-0 focus-visible:border-[#E60000]"
                />
              </div>
            </div>
            )}
            {!anonymous && (
            <div>
              <Label htmlFor="dmsg" className="text-xs uppercase tracking-[0.25em] font-bold">Message (optional)</Label>
              <Textarea
                id="dmsg"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="donate-message-input"
                placeholder="A note of support..."
                className="rounded-none border-2 border-black mt-2 min-h-[80px] focus-visible:ring-0 focus-visible:border-[#E60000]"
              />
            </div>
            )}
            {anonymous && (
              <p className="text-xs text-neutral-600 font-medium" data-testid="donate-anonymous-note">
                We'll send you straight to the secure Stripe page. Stripe asks for an email only to send your receipt — nothing personal is stored here.
              </p>
            )}
            <Button
              type="submit"
              disabled={submitting}
              data-testid="donate-submit-btn"
              className="w-full h-12 rounded-none bg-[#E60000] hover:bg-black text-white border-2 border-black font-bold uppercase tracking-widest text-sm"
            >
              {submitting
                ? "Redirecting to Stripe…"
                : frequency === "monthly"
                  ? `Start $${monthlyTier}/month Donation`
                  : `Donate Now${amount ? ` — $${amount}` : ""}`}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Testimonials = () => {
  const items = [
    {
      name: "Marcus R.",
      role: "Former pro grinder — 8 yrs",
      image: IMAGES.testimonial1,
      quote: "I lost $214k over six years. The ebook gave me the exact script I needed to delete every account in 48 hours. 11 months clean.",
      stat: "$214,000 saved",
    },
    {
      name: "Priya K.",
      role: "Recreational player — 4 yrs",
      image: IMAGES.testimonial2,
      quote: "The ebook hit harder than any therapist I tried. I cried, I deleted the app, and I haven't opened a hand since.",
      stat: "9 months clean",
    },
  ];
  return (
    <Section id="testimonials" className="border-b-2 border-black bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-14">
          <div className="lg:col-span-8">
            <Reveal>
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#E60000] mb-4">Real stories. Real numbers.</div>
              <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9]">
                They were where<br /> you are now.
              </h2>
            </Reveal>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 border-2 border-black">
          {items.map((t, idx) => (
            <article
              key={t.name}
              data-testid={`testimonial-${idx}`}
              className={`p-8 lg:p-12 ${idx === 0 ? "md:border-r-2 border-black" : ""}`}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 border-2 border-black overflow-hidden bg-neutral-200">
                  <img src={t.image} alt={t.name} className="w-full h-full object-cover img-grayscale" />
                </div>
                <div>
                  <div className="font-display text-2xl uppercase">{t.name}</div>
                  <div className="text-xs uppercase tracking-widest text-neutral-600 font-bold">{t.role}</div>
                </div>
              </div>
              <blockquote className="font-display text-2xl lg:text-3xl uppercase leading-tight">
                "{t.quote}"
              </blockquote>
              <div className="mt-8 inline-block border-2 border-black bg-[#E60000] text-white px-4 py-2 font-bold uppercase tracking-widest text-xs">
                {t.stat}
              </div>
            </article>
          ))}
        </div>
      </div>
    </Section>
  );
};

const AuthorBio = () => (
  <Section id="author" className="border-b-2 border-black bg-[#F5F2EC]">
    <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12">
      <div className="lg:col-span-5 border-r-0 lg:border-r-2 border-black">
        <Reveal>
          <div className="aspect-square w-full overflow-hidden">
            <img
              src={IMAGES.bio}
              alt="Author"
              className="w-full h-full object-cover"
              data-testid="author-image"
            />
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-7 px-6 lg:px-12 py-16 lg:py-20">
        <Reveal>
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#E60000] mb-4">Notes from the author</div>
          <h2 className="font-display text-5xl sm:text-6xl uppercase leading-[0.9]">
            I lost it all<br />many times<br />so you<br />don't have to.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-8 space-y-5 text-base lg:text-lg font-medium text-neutral-800 max-w-2xl leading-relaxed">
            <p>
              Breaking a pattern that has lived inside a person for nearly thirty
              years is one of the hardest things any human being can attempt. For
              me, gambling addiction wasn't just a habit; it became the lens
              through which I saw everything. It was the system that shaped my
              decisions, my emotions, my identity. And that system dragged me
              through homelessness, hunger, cold, heat, incarceration, scamming
              people, losing friends, destroying relationships, hurting my
              family, and ultimately hurting myself until the day I found the
              secret that has changed my life forever.
            </p>
            <p className="font-display text-2xl lg:text-3xl uppercase leading-tight text-[#E60000]">
              A secret I want to share with you.
            </p>
            <p>
              Once that secret came into the light, everything changed. That was
              the moment my real recovery journey began. That was the moment I
              stopped being a prisoner of the cycle and started becoming the
              person I was always meant to be.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-10">
            <motion.a
              href="#products"
              data-testid="author-cta"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.96 }}
              className="inline-flex items-center gap-2 bg-[#E60000] text-white border-2 border-black px-8 py-4 font-bold uppercase tracking-widest text-sm shadow-brutal-sm shadow-brutal-hover"
            >
              Discover The Secret — Get The Book Free
            </motion.a>
          </div>
        </Reveal>
      </div>
    </div>
  </Section>
);

const FAQ = () => {
  const faqs = [
    {
      q: "Is this a real medical or therapy program?",
      a: "No. This is a recovery field-manual built from lived experience and behavioral patterns. It is not a substitute for professional care. If you are in crisis, please call your local helpline.",
    },
    {
      q: "Is it really free? What's included?",
      a: "Yes — completely free, forever. The full ebook (PDF): a 12-chapter recovery field manual with craving-defeat scripts and a financial reset workbook. Instant download, plus a copy in your inbox.",
    },
    {
      q: "How is it delivered?",
      a: "Instantly — right on this page the moment you claim it, and to the email address you provide so you have the link forever. Check spam if you don't see it within 5 minutes.",
    },
    {
      q: "Do you offer refunds?",
      a: "The book is free, so there's nothing to refund. For donations: if you made a mistake, reply to your receipt email and we'll make it right — no questions, no judgment.",
    },
    {
      q: "Why is the donation optional?",
      a: "Because guilt-tripping people in recovery is the opposite of what we do. The book is free. Donations are the only thing keeping the lights on — they let us put this in front of the next person who can't afford help.",
    },
    {
      q: "Will my information be private?",
      a: "Absolutely. We never share your email, never resell data, and never publish purchase records. Recovery is private work.",
    },
  ];
  return (
    <Section id="faq" className="border-b-2 border-black bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#E60000] mb-4">Common questions</div>
            <h2 className="font-display text-5xl lg:text-6xl uppercase leading-[0.9]">
              Doubts?<br /><span className="text-[#E60000]">Good.</span><br />Read on.
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          <Reveal delay={0.15}>
            <Accordion type="single" collapsible className="w-full border-t-2 border-black" data-testid="faq-accordion">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b-2 border-black"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="font-display text-2xl lg:text-3xl uppercase tracking-tight py-6 hover:no-underline hover:text-[#E60000] text-left"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base lg:text-lg font-medium text-neutral-800 pb-6 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </Section>
  );
};

const Footer = ({ onDonate }) => {
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Enter a valid email.");
    try {
      await newsletterSignup(email);
      toast.success("You're on the list.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Try again.");
    }
  };
  return (
    <footer className="bg-[#0A0A0A] text-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 pb-8">
        <motion.h2
          className="font-display uppercase leading-[0.85] text-7xl sm:text-8xl lg:text-[14vw] tracking-tighter"
          data-testid="footer-headline"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-70px" }}
          transition={{ duration: 1, ease: EASE }}
        >
          It Ends <span className="text-[#E60000]">Today.</span>
        </motion.h2>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-16 pt-10 border-t-2 border-white/20">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <LogoMark size={30} dark />
              <span className="font-display text-2xl uppercase">The Last Bet Project</span>
            </div>
            <p className="text-neutral-400 max-w-md">
              A small, independent recovery project. Funded by readers and
              donors. Not affiliated with any casino, sportsbook, or treatment
              center.
            </p>
            <p className="mt-4 text-xs uppercase tracking-widest font-bold text-neutral-500 max-w-md">
              Our projects:{" "}
              <Link to="/poker" data-testid="footer-project-poker" className="text-white hover:text-[#E60000] transition-colors">Anti Poker Addiction</Link>
              {" · "}
              <Link to="/gambling" data-testid="footer-project-gambling" className="text-white hover:text-[#E60000] transition-colors">Anti Gambling Addiction</Link>
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <motion.button
                onClick={onDonate}
                data-testid="footer-donate-btn"
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 bg-[#E60000] text-white border-2 border-white px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-white hover:text-black transition-colors"
              >
                Donate <HeartHandshake className="w-4 h-4" />
              </motion.button>
              <ContactButton dark />
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-widest font-bold text-[#E60000] mb-4">Sections</div>
            <ul className="space-y-2 text-sm">
              <li><a href="#products" className="hover:text-[#E60000]">Get Help</a></li>
              <li><a href="#author" className="hover:text-[#E60000]">Story</a></li>
              <li><a href="#author" className="hover:text-[#E60000]">Author</a></li>
              <li><a href="#faq" className="hover:text-[#E60000]">FAQ</a></li>
            </ul>
          </div>
          <div className="lg:col-span-4">
            <div className="text-xs uppercase tracking-widest font-bold text-[#E60000] mb-4">Get recovery tips</div>
            <form onSubmit={submit} className="flex gap-0 border-2 border-white" data-testid="newsletter-form">
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email-input"
                className="flex-1 px-4 py-3 bg-transparent outline-none text-white placeholder-neutral-500"
              />
              <button
                type="submit"
                data-testid="newsletter-submit-btn"
                className="bg-[#E60000] px-4 py-3 font-bold uppercase tracking-widest text-xs border-l-2 border-white hover:bg-white hover:text-black transition-colors"
              >
                Join
              </button>
            </form>
            <p className="mt-3 text-xs text-neutral-500">No spam. Unsubscribe anytime.</p>
          </div>
        </div>
        <div className="mt-12 border-2 border-white/20 p-5 text-xs text-neutral-400 font-medium leading-relaxed" data-testid="merchant-info">
          <span className="uppercase tracking-widest font-bold text-[#E60000]">Merchant information. </span>
          The Last Bet Project — an independent recovery project offering free digital
          books and accepting voluntary donations. Payments are processed securely by
          Stripe. One-time donations are non-refundable except in cases of clear error
          (contact us and we'll make it right); monthly support can be canceled anytime.
          Questions about the books, the site, or a donation? Use the Contact Us button
          or read our{" "}
          <Link to="/terms" className="text-white hover:text-[#E60000] transition-colors">Terms of Service</Link> and{" "}
          <Link to="/privacy" className="text-white hover:text-[#E60000] transition-colors">Privacy Policy</Link>.
        </div>
        <div className="mt-12 pt-6 border-t-2 border-white/20 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-neutral-500 uppercase tracking-widest font-bold">
          <span>© {new Date().getFullYear()} The Last Bet Project</span>
          <span className="flex gap-6">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#E60000] transition-colors">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[#E60000] transition-colors">Terms of Service</Link>
          </span>
          <span>If you're in crisis, call your local helpline.</span>
        </div>
      </div>
    </footer>
  );
};

const FreeBookDialog = ({ claim, onClose }) => (
  <Dialog open={!!claim} onOpenChange={(o) => !o && onClose()}>
    <DialogContent
      className="rounded-none border-2 border-black bg-white p-0 max-w-lg shadow-brutal max-h-[92vh] overflow-y-auto"
      data-testid="free-book-dialog"
      hideClose
    >
      <button
        onClick={onClose}
        className="absolute right-3 top-3 z-10 border-2 border-black w-9 h-9 flex items-center justify-center bg-white hover:bg-black hover:text-white transition-colors"
        aria-label="Close"
        data-testid="free-book-close"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="bg-[#E60000] text-white p-6 pr-16 border-b-2 border-black">
        <div className="text-xs uppercase tracking-[0.3em] font-bold mb-2">The first move is made</div>
        <DialogTitle className="font-display text-4xl uppercase leading-none">
          Welcome.<br />This is how<br />liberation starts.
        </DialogTitle>
      </div>
      <div className="p-6">
        <DialogDescription className="text-base font-medium text-neutral-800 leading-relaxed">
          What you just did is bigger than a download{claim?.name ? `, ${claim.name}` : ""}.
          You reached for help — and that single move is the first crack in a
          disease that survives on silence and shame. We built this project for
          exactly this moment. Welcome to your recovery.
        </DialogDescription>
        <div className="mt-6 border-2 border-black bg-[#F5F2EC] p-5" data-testid="free-book-email-note">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#E60000]">Check your email</div>
          <p className="mt-2 text-sm font-medium text-neutral-800 leading-relaxed">
            <span className="font-bold text-black">{claim?.title || "Your book"}</span> is on its
            way to <span className="font-bold text-black">{claim?.email}</span>.
            Open the email and tap the <span className="font-bold text-black">Download</span> button
            at the bottom — the book is yours.
          </p>
          <p className="mt-3 text-xs uppercase tracking-widest font-bold text-neutral-600">
            Nothing in 5 minutes? Check your spam folder.
          </p>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export default function Landing() {
  const [donateOpen, setDonateOpen] = useState(false);
  const [claim, setClaim] = useState(null);
  const [introDone, setIntroDone] = useState(false);
  const [stats, setStats] = useState({ total: 0, count: 0 });
  const lenisRef = useRef(null);

  // Lenis momentum scrolling + smooth anchor routing
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    const onClick = (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;
      const hash = anchor.getAttribute("href");
      if (hash && hash.length > 1) {
        const el = document.querySelector(hash);
        if (el) {
          e.preventDefault();
          lenis.scrollTo(el, { offset: -72 });
        }
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener("click", onClick);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  // Freeze background momentum scroll while a dialog is open
  useEffect(() => {
    const lenis = lenisRef.current;
    if (!lenis) return;
    if (donateOpen || claim) lenis.stop();
    else lenis.start();
  }, [donateOpen, claim]);

  // Email "Donate" button lands on /#donate — open the form directly, no extra steps
  useEffect(() => {
    if (window.location.hash === "#donate") {
      setDonateOpen(true);
    }
  }, []);

  const refreshStats = async () => {
    try {
      const s = await getDonationStats();
      setStats(s);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    refreshStats();
  }, []);

  const handleBuy = (newClaim) => {
    setClaim(newClaim);
    toast.success("Your book is on its way — check your inbox.");
  };

  return (
    <div className="min-h-screen bg-white font-body text-black" data-testid="landing-root">
      <div className="grain-overlay" aria-hidden="true" />
      <AnimatePresence>
        {!introDone && <IntroOverlay onDone={() => setIntroDone(true)} />}
      </AnimatePresence>
      <Header onDonate={() => setDonateOpen(true)} />
      <Hero />
      <AuthorBio />
      <MarqueeBand />
      <Products onBuy={handleBuy} />
      <Donation open={donateOpen} setOpen={setDonateOpen} stats={stats} refreshStats={refreshStats} />
      <FAQ />
      <Footer onDonate={() => setDonateOpen(true)} />
      <FreeBookDialog
        claim={claim}
        onClose={() => setClaim(null)}
      />
    </div>
  );
}
